import { Search, MapPin, Youtube, Facebook, Megaphone, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useTypingAnimation } from '@/hooks/use-typing-animation';
import { useFadeInObserver } from '@/hooks/use-fade-in-observer';

const ServiceCard = ({ service, index }: { service: any; index: number }) => {
  const { ref, isVisible } = useFadeInObserver(index * 100);
  const Icon = service.icon;

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-8'
      }`}
    >
      <Card className="card-hover border-border bg-card group h-full">
        <CardHeader>
          <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 transition-all duration-300 group-hover:bg-primary/20 group-hover:scale-110">
            <Icon className="w-7 h-7 text-primary icon-bounce" />
          </div>
          <CardTitle className="text-xl transition-colors group-hover:text-primary">{service.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-base mb-4">{service.description}</CardDescription>
          <p className="text-sm font-semibold text-primary">{service.projects}</p>
        </CardContent>
      </Card>
    </div>
  );
};

const ServicesSection = () => {
  const { ref: headingRef, displayedText } = useTypingAnimation('Boost Business Strategic Solutions', 60);
  
  const services = [
    {
      icon: Search,
      title: 'Search Engine Optimization',
      description: 'Improving search engine rankings and driving organic traffic through proven SEO strategies.',
      projects: '10+ Projects',
    },
    {
      icon: MapPin,
      title: 'Local SEO',
      description: 'Optimizing your business for local search results and Google Maps to attract nearby customers.',
      projects: '10+ Projects',
    },
    {
      icon: Youtube,
      title: 'YouTube SEO',
      description: 'Maximizing video visibility and channel growth through strategic YouTube optimization.',
      projects: '30+ Projects',
    },
    {
      icon: Facebook,
      title: 'Meta Ads',
      description: 'Creating high-converting Facebook and Instagram ad campaigns to reach your target audience.',
      projects: '20+ Projects',
    },
    {
      icon: Megaphone,
      title: 'Digital Marketing',
      description: 'Comprehensive digital marketing solutions to boost your online presence and conversions.',
      projects: '50+ Projects',
    },
    {
      icon: TrendingUp,
      title: 'Business Strategy',
      description: 'Developing data-driven strategies to accelerate business growth and maximize ROI.',
      projects: '5+ Projects',
    },
  ];

  return (
    <section id="services" className="py-24 bg-secondary/30">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16 animate-fade-in">
          <p className="text-primary font-semibold mb-2 uppercase tracking-wider text-sm">Services</p>
          <h2 ref={headingRef as any} className="text-4xl md:text-5xl font-bold mb-4 min-h-[3rem]">
            {displayedText}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Business consulting consultants provide expert advice and guidance to businesses to help
            them improve their performance and efficiency.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
